/**
 * Widget Skeleton Component
 *
 * Feature: 136-dashboard-alignment
 * Description: Reusable skeleton loader for progressive dashboard widget loading.
 *   Card shape with shimmer animation for consistent loading states.
 * Constitution: v1.12.0 — Principle XXXVII (Code Header Standards)
 */

import React from 'react';
import clsx from 'clsx';

interface WidgetSkeletonProps {
  /** Number of shimmer rows to display (default: 4) */
  rows?: number;
  /** Optional CSS class for the outer container */
  className?: string;
  /** Whether to include a chart-shaped placeholder (default: false) */
  showChart?: boolean;
  /** Title text rendered during loading (default: none) */
  title?: string;
}

/**
 * Reusable skeleton loader for dashboard widgets.
 * Renders a card container with animated shimmer rows.
 */
export const WidgetSkeleton: React.FC<WidgetSkeletonProps> = ({
  rows = 4,
  className,
  showChart = false,
  title,
}) => (
  <div
    className={clsx(
      'bg-[var(--color-bg-card)] rounded-lg border border-[var(--color-border)] p-4 animate-pulse',
      className
    )}
    role="status"
    aria-label="Loading widget"
  >
    {/* Header */}
    {title ? (
      <div className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider mb-4">
        {title}
      </div>
    ) : (
      <div className="h-4 bg-[var(--color-bg-subtle)] rounded w-1/3 mb-4" />
    )}

    {/* Chart placeholder */}
    {showChart && (
      <div className="h-32 bg-[var(--color-bg-subtle)] rounded mb-4" />
    )}

    {/* Row placeholders */}
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center space-x-3">
          <div className="w-2 h-2 rounded-full bg-[var(--color-bg-subtle)] flex-shrink-0" />
          <div className="flex-1">
            <div
              className="h-4 bg-[var(--color-bg-subtle)] rounded"
              style={{ width: `${70 + Math.random() * 30}%` }}
            />
          </div>
          <div className="h-4 bg-[var(--color-bg-subtle)] rounded w-12 flex-shrink-0" />
        </div>
      ))}
    </div>

    <span className="sr-only">Loading...</span>
  </div>
);

export default WidgetSkeleton;
